import { useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '../lib/cn'
import {
  JALALI_MONTHS,
  JALALI_WEEKDAYS_SHORT,
  dateStringToJalali,
  jalaliMonthLength,
  jalaliMonthStartWeekday,
  jalaliToDateString,
  toPersianDigits,
  todayJalali,
} from '../lib/date'
import { useField } from './Field'

/* ------------------------------------------------------------------
   انتخابگر تاریخ شمسی — طرح Stitch «Premium Minimal Jalali Date Picker»
   ------------------------------------------------------------------
   🔴 چرا این کامپوننت ساخته شد:

   فرم‌ها از `<input type="date">` استفاده می‌کردند — یعنی تقویم
   **میلادی** مرورگر. کاربر ایرانی باید در ذهنش تبدیل می‌کرد که
   «۱۵ مرداد» یعنی کدام روز میلادی، و هر بار احتمال اشتباه داشت.

   ------------------------------------------------------------------
   🔴 تقویم وسط صفحه باز می‌شود و نه چسبیده زیر ورودی.

   پیش‌تر با `absolute` داخل جریان صفحه باز می‌شد. یعنی هر والدی که
   `overflow: hidden` داشت — بدنه Card، سطر جدول، نوار فیلتر — تقویم
   را می‌بُرید. حالا با Portal به `document.body` می‌رود.

   ⚠️ در موبایل از پایین بالا می‌آید (`items-end` + گوشه‌های بالا گرد) و
   در دسکتاپ وسط می‌نشیند — همان چیزی که طرح Stitch می‌خواهد.

   ------------------------------------------------------------------
   🔴 انتخاب با یک کلیک ثبت می‌شود — بدون دکمه «تأیید».

   طرح Stitch دکمه تأیید داشت و در نسخه‌ای از این کامپوننت هم بود، ولی
   برای انتخابگری که کارش فقط گرفتن یک تاریخ است، مرحله تأییدِ اضافه
   هر انتخاب را دو کلیکی می‌کرد بی‌آنکه چیزی در ازایش بدهد.

   ⚠️ با حذف آن، «پیش‌نویس» هم حذف شد: دیگر حالت میانی‌ای وجود ندارد
   که بخواهد نگه داشته شود، پس مقدار مستقیم از `value` خوانده می‌شود.
   بستن بدون انتخاب با «بستن»، کلیک بیرون یا Escape انجام می‌شود.

   ------------------------------------------------------------------
   ⚠️ رنگ انتخاب: طرح Stitch روی `#03b585` متن **سفید** گذاشته بود.
   نسبت کنتراست آن ۲٫۶۴ است — زیر حد ۴٫۵ و ناخواناست. همان `#03b585`
   نگه داشته شد (توکن `--pf-primary-container`) ولی متنش
   `--pf-on-primary-container` شد؛ دقیقاً همان جفتی که خودِ
   `tokens.css` برای این پس‌زمینه تعیین کرده و ۴٫۵۵ می‌دهد.

   ------------------------------------------------------------------
   ⚠️ مقدار ورودی و خروجی همیشه **میلادیِ `YYYY-MM-DD`** است.

   یعنی هیچ چیزی در Backend یا در بقیه فرم عوض نمی‌شود؛ فقط آنچه کاربر
   می‌بیند شمسی است. اگر مقدار هم شمسی می‌شد، هر مصرف‌کننده باید
   می‌دانست کدام فیلد شمسی است و کدام میلادی — منبع بی‌پایان اشتباه.
   ------------------------------------------------------------------ */

/**
 * تعداد سطرهای جدول تقویم — ثابت.
 *
 * ⚠️ بیشترین حالت ممکن است و نه اندازه واقعی هر ماه: ماهی با ۳۱ روز که
 * از جمعه شروع شود ۶ سطر می‌خواهد. ثابت‌نگه‌داشتنش تنها راهی است که
 * ارتفاع کارت با عوض‌کردن ماه نپرد.
 */
const CALENDAR_ROWS = 6

/**
 * بازه پیش‌فرض فهرست سال — وقتی `min`/`max` داده نشده باشد.
 *
 * ⚠️ عقب‌تر از جلو باز است چون بیشترِ تاریخ‌های بی‌کران در این سامانه
 * گذشته‌اند (تاریخ تولد، تاریخ صدور مدرک) و آینده‌شان معمولاً `max`
 * دارد.
 */
const YEARS_BACK = 90
const YEARS_FORWARD = 20

/** کدام نما در بدنه کارت دیده می‌شود. */
type PickerMode = 'day' | 'month' | 'year'

/**
 * تعطیلات رسمی — کلید میلادیِ `YYYY-MM-DD` و مقدار عنوانِ مناسبت.
 *
 * ⚠️ `Map` است و نه آرایه: تقویم برای هر خانه‌ای که رسم می‌کند یک بار
 * پرسش می‌کند (تا ۳۱ بار در هر ماه). با آرایه هر پرسش یک `find` خطی
 * می‌شد روی فهرستی که برای یک سال ~۲۵ عضو دارد.
 */
export type HolidayMap = ReadonlyMap<string, string | null>

export interface JalaliDatePickerProps {
  value: string | null
  onChange: (value: string) => void
  /** کف مجاز — میلادیِ `YYYY-MM-DD` */
  min?: string | null
  /** سقف مجاز — میلادیِ `YYYY-MM-DD` */
  max?: string | null
  /*
   | تعطیلات رسمی برای نمایش.
   |
   | 🔴 عمداً اختیاری و از بیرون داده می‌شود و نه اینکه خودِ تقویم
   | بگیردش. دو دلیل:
   |
   | ⚠️ یک) این پکیج حق ندارد ماژول کسب‌وکار را بشناسد (سند مادر ۵.۲).
   | صداکردن `calendarApi` از اینجا یعنی ui-kit به تقویمِ سازمانی
   | گره بخورد.
   |
   | ⚠️ دو) این انتخابگر ~۳۰ جا استفاده می‌شود که بیشترشان — تاریخ
   | تولد، تاریخ صدور مدرک، فیلترِ گزارش — تعطیلی برایشان بی‌معناست.
   | خودکارکردنش یعنی ۳۰ درخواست شبکه برای چیزی که چند صفحه
   | می‌خواهند.
   */
  holidays?: HolidayMap
  placeholder?: string
  /** عنوان بالای پنجره — وقتی چند تاریخ در یک فرم هست، تفکیک‌کننده است */
  title?: string
  /**
   * زیرعنوان توضیحی زیر عنوان.
   *
   * ⚠️ پیش‌فرض ندارد. جمله عمومیِ «تاریخ مورد نظر خود را انتخاب کنید»
   * چیزی به کاربری که تقویم جلویش باز است اضافه نمی‌کرد و فقط یک سطر
   * از ارتفاع کارت را می‌گرفت.
   */
  description?: string
  disabled?: boolean
  invalid?: boolean
  className?: string
  id?: string
}

export function JalaliDatePicker({
  value,
  onChange,
  min,
  max,
  holidays,
  placeholder = 'انتخاب تاریخ',
  title = 'انتخاب تاریخ',
  description,
  disabled,
  invalid,
  className,
  id,
}: JalaliDatePickerProps) {
  const field = useField()
  const isInvalid = invalid ?? field?.invalid ?? false

  const [open, setOpen] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  /*
   | نمای فعال — روز، ماه یا سال.
   |
   | 🔴 بدون این، رسیدن به «تاریخ تولد» یعنی صدها بار زدن «ماه قبل».
   | برای کسی که ۱۳۶۵ متولد شده، از امروز ~۴۷۰ کلیک فاصله است.
   */
  const [mode, setMode] = useState<PickerMode>('day')

  /* سالِ در حال نمایش — تا فهرست سال روی آن باز شود و نه از ابتدا. */
  const scrollTargetRef = useRef<HTMLButtonElement>(null)

  const selected = useMemo(() => dateStringToJalali(value), [value])
  const today = useMemo(() => todayJalali(), [])

  /* ماهی که تقویم نشان می‌دهد — مستقل از مقدار انتخاب‌شده. */
  const [view, setView] = useState(() => {
    const base = selected ?? today

    return { jy: base.jy, jm: base.jm }
  })

  /*
   | باز کردن — همیشه روی ماهِ مقدار فعلی باز می‌شود.
   |
   | ⚠️ اینجا انجام می‌شود و نه در `useEffect`: با Effect باید `value`
   | را از فهرست وابستگی‌ها بیرون می‌گذاشتیم، وگرنه تغییرِ بیرونیِ
   | مقدار وسط کار، ماهی را که کاربر داشت می‌دید عوض می‌کرد.
   */
  const openPicker = () => {
    const base = dateStringToJalali(value) ?? today

    setView({ jy: base.jy, jm: base.jm })

    /* ⚠️ همیشه از نمای روز باز می‌شود، حتی اگر بار قبل روی سال بسته شده. */
    setMode('day')
    setOpen(true)
  }

  useEffect(() => {
    if (!open) return

    /*
     | ⚠️ Escape با `capture` گرفته می‌شود تا اگر تقویم داخل یک Dialog
     | باز شده باشد، اول این ببندد و نه پنجره بیرونی — وگرنه کاربر با
     | یک Escape کل فرمش را از دست می‌دهد.
     */
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        setOpen(false)
      }
    }

    /* اسکرول پس‌زمینه قفل می‌شود تا تقویمِ شناور با صفحه جابه‌جا نشود. */
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'

    const focusTimer = window.setTimeout(() => panelRef.current?.focus(), 0)

    document.addEventListener('keydown', onKeyDown, true)

    return () => {
      window.clearTimeout(focusTimer)
      document.removeEventListener('keydown', onKeyDown, true)
      document.body.style.overflow = overflow

      /* فوکوس به همان دکمه‌ای برمی‌گردد که تقویم را باز کرده بود. */
      triggerRef.current?.focus()
    }
  }, [open])

  /*
   | فهرست سال روی سالِ فعلی باز می‌شود.
   |
   | ⚠️ `block: 'center'` لازم است و نه `nearest`: بازه می‌تواند ۱۱۰ سال
   | باشد و سالِ فعلی معمولاً وسط آن است. بدون این، کاربر ابتدای فهرست
   | (۹۰ سال پیش) را می‌بیند و فکر می‌کند سال خودش اصلاً نیست.
   */
  useEffect(() => {
    if (mode !== 'year') return

    scrollTargetRef.current?.scrollIntoView({ block: 'center' })
  }, [mode])

  const label = selected
    ? `${toPersianDigits(selected.jy)}/${toPersianDigits(String(selected.jm).padStart(2, '0'))}/${toPersianDigits(String(selected.jd).padStart(2, '0'))}`
    : null

  /*
   | نامِ مناسبتِ روزِ انتخاب‌شده — نواری که زیر تقویم می‌آید.
   |
   | 🔴 فقط قرمزکردنِ عدد می‌گوید «تعطیل است» ولی نمی‌گوید «چرا». برای
   | کسی که دارد مرخصی ثبت می‌کند، «۱۳ فروردین» و «سیزده‌بدر» دو خبرِ
   | متفاوتند.
   |
   | ⚠️ فقط وقتی نشان داده می‌شود که عنوان داشته باشد؛ تعطیلیِ بی‌نام
   | نواری خالی می‌ساخت که ارتفاع می‌گرفت و چیزی نمی‌گفت.
   */
  const selectedHolidayTitle =
    value && holidays ? (holidays.get(value) ?? null) : null

  const monthLength = jalaliMonthLength(view.jy, view.jm)
  const startWeekday = jalaliMonthStartWeekday(view.jy, view.jm)

  const todayIso = jalaliToDateString(today.jy, today.jm, today.jd)
  const todayOutOfRange = (min != null && todayIso < min) || (max != null && todayIso > max)

  /** آیا این روز بیرون از بازه مجاز است؟ */
  const outOfRange = (jd: number): boolean => {
    const iso = jalaliToDateString(view.jy, view.jm, jd)

    if (min != null && iso < min) return true
    if (max != null && iso > max) return true

    return false
  }

  /*
   | آیا کل این ماه بیرون از بازه مجاز است؟
   |
   | ⚠️ مقایسه با **کف و سقفِ خودِ ماه** انجام می‌شود و نه با روز اول:
   | اگر `min` وسط ماه باشد، ماه هنوز روزهای مجاز دارد و نباید خاکستری
   | شود. تنها وقتی ماه بسته است که آخرین روزش پیش از `min` یا اولین
   | روزش پس از `max` باشد.
   */
  const monthOutOfRange = (jy: number, jm: number): boolean => {
    const first = jalaliToDateString(jy, jm, 1)
    const last = jalaliToDateString(jy, jm, jalaliMonthLength(jy, jm))

    if (min != null && last < min) return true
    if (max != null && first > max) return true

    return false
  }

  /** آیا کل این سال بیرون از بازه مجاز است؟ — به همان منطق ماه. */
  const yearOutOfRange = (jy: number): boolean => {
    const first = jalaliToDateString(jy, 1, 1)
    const last = jalaliToDateString(jy, 12, jalaliMonthLength(jy, 12))

    if (min != null && last < min) return true
    if (max != null && first > max) return true

    return false
  }

  /*
   | بازه سال‌های قابل انتخاب.
   |
   | 🔴 چرا این‌قدر باز: همین کامپوننت هم «تاریخ تولد» را می‌گیرد (تا ~۸۰
   | سال عقب) و هم «تاریخ انقضا» را (سال‌ها جلو). بازه باریک یعنی کاربر
   | به دیوار بخورد و راهی نداشته باشد.
   |
   | ⚠️ اگر `min`/`max` داده شده باشد بازه به همان‌ها بریده می‌شود — نشان
   | دادن سالی که همه روزهایش غیرمجازند فقط کلیکِ بی‌نتیجه می‌سازد.
   */
  const years = useMemo(() => {
    const minJalali = dateStringToJalali(min ?? null)
    const maxJalali = dateStringToJalali(max ?? null)

    const from = minJalali?.jy ?? today.jy - YEARS_BACK
    const to = maxJalali?.jy ?? today.jy + YEARS_FORWARD

    return Array.from({ length: Math.max(1, to - from + 1) }, (_, i) => from + i)
  }, [min, max, today.jy])

  /** جابه‌جایی ماه — با سرریز به سال قبل/بعد. */
  const step = (delta: number) => {
    setView((current) => {
      const raw = current.jm + delta

      if (raw < 1) return { jy: current.jy - 1, jm: 12 }
      if (raw > 12) return { jy: current.jy + 1, jm: 1 }

      return { jy: current.jy, jm: raw }
    })
  }

  /*
   | خانه‌های جدول — همیشه دقیقاً ۶ سطر.
   |
   | ماه شمسی بسته به طولش و روزی از هفته که با آن شروع می‌شود، در ۵ یا
   | ۶ سطر جا می‌گیرد. اگر جدول به اندازه نیاز رشد کند، با هر «ماه بعد»
   | ارتفاع کارت یک سطر می‌پرد و دکمه‌های پایین زیر انگشت کاربر جابه‌جا
   | می‌شوند.
   */
  const cells: (number | null)[] = Array.from({ length: CALENDAR_ROWS * 7 }, (_, index) => {
    const day = index - startWeekday + 1

    return day >= 1 && day <= monthLength ? day : null
  })

  return (
    <>
      {/* ---------- کنترل بسته ---------- */}
      <button
        ref={triggerRef}
        type="button"
        id={id ?? field?.id}
        disabled={disabled}
        onClick={openPicker}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        /*
          ⚠️ همان مدل «Filled» بقیهٔ کنترل‌ها — زمینهٔ ملایم و خط زیرین.
          عمداً دستی تکرار می‌شود و از `controlBase` نمی‌آید: آن تابع
          صادر نشده و صادرکردنش یعنی یک API عمومیِ تازه در پکیج برای
          چیزی که فقط دو مصرف‌کننده دارد. ولی هر تغییری آنجا باید
          اینجا هم بیاید، وگرنه تقویم از بقیهٔ فرم جدا می‌افتد.
        */
        className={cn(
          'flex h-9.5 w-full items-center justify-between gap-2',
          /* ⚠️ سه طرف صریح شفاف — با `border-transparent` سراسری، خطِ
             زیرین به‌خاطر ترتیبِ خروجی Tailwind رنگ نمی‌گرفت. */
          'rounded-t-[var(--pf-radius)] rounded-b-[var(--pf-radius-sm)] px-3',
          'border border-x-transparent border-t-transparent',
          'text-start text-[length:var(--pf-text-md)]',
          'transition-[background-color,border-color] duration-[var(--pf-duration-fast)]',
          isInvalid
            ? 'bg-[var(--pf-danger-50)] border-b-2 border-b-[var(--pf-danger-500)]'
            : cn(
                /* ۲px و `--pf-text-subtle` — ۱px با `border-strong` روی
                   زمینهٔ خاکستریِ فیلد اصلاً دیده نمی‌شد. */
                'bg-[var(--pf-surface-muted)] border-b-2 border-b-[var(--pf-text-subtle)]',
                !disabled && 'hover:bg-[var(--pf-surface-hover)]',
                'focus-visible:border-b-[var(--pf-primary-container)]',
                /* تقویمِ باز = فیلدِ فعال. */
                open && 'border-b-[var(--pf-primary-container)]',
              ),
          disabled && 'cursor-not-allowed opacity-60',
          className,
        )}
      >
        <span
          className={cn(
            'truncate',
            label ? 'text-[var(--pf-text)]' : 'text-[var(--pf-text-subtle)]',
          )}
        >
          {label ?? placeholder}
        </span>

        <svg
          viewBox="0 0 24 24"
          fill="none"
          className="size-4 shrink-0 text-[var(--pf-text-subtle)]"
          aria-hidden="true"
        >
          <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
          <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
        </svg>
      </button>

      {open &&
        createPortal(
          /*
            ⚠️ در موبایل `items-end` و در دسکتاپ `items-center` — طبق طرح
            Stitch کارت در موبایل از لبه پایین بالا می‌آید و تمام‌عرض
            می‌شود، چون رساندن انگشت به وسط صفحه سخت‌تر از لبه پایین است.
          */
          <div
            className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4"
            style={{ zIndex: 'var(--pf-z-dialog)' }}
          >
            {/*
              ⚠️ رنگ و شفافیت و بلور هر سه از توکن می‌آیند و نه از
              کلاس‌های ثابت. `color-mix` به جای نحو `/40` تیلویند به‌کار
              رفته چون آن نحو عدد ثابت می‌خواهد و نمی‌تواند درصد را از
              متغیر بخواند — و درصد باید در پوسته تاریک فرق کند.
            */}
            <div
              data-state="open"
              className="pf-scrim absolute inset-0"
              style={{
                backgroundColor:
                  'color-mix(in srgb, var(--pf-scrim) var(--pf-scrim-opacity), transparent)',
                backdropFilter: 'blur(var(--pf-scrim-blur))',
                WebkitBackdropFilter: 'blur(var(--pf-scrim-blur))',
              }}
              onClick={() => setOpen(false)}
              aria-hidden="true"
            />

            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-label={title}
              tabIndex={-1}
              /* ورود مثلِ Dialog (‎.pf-sheet‎ در tokens.css): کشو در گوشی، کارت در دسکتاپ.
                 بستن عمداً فوری است — انتخابِ تاریخ/ساعت باید همان لحظه نتیجه بدهد. */
              data-state="open"
              /*
                🔴 در موبایل کشو است و نه کارت.

                سقف عرض عمداً از `sm:` شروع می‌شود. وقتی `max-w` بدون
                پیشوند بود، در موبایل هم اعمال می‌شد و یک کارت ۳۲۰
                پیکسلی وسطِ لبه پایین می‌نشست — با دو نوار خالی کنارش.
                حالا در موبایل تمام‌عرض است و از `sm:` به بعد به ۳۲۰px
                محدود می‌شود.

                ⚠️ عرض ۳۲۰ پیکسل (و نه ۴۰۰ پیکسلِ طرح Stitch) فقط برای
                دسکتاپ است: طرح برای نمایشِ تکی کشیده شده بود، ولی این
                یک انتخابگر داخل فرم است.
              */
              className={cn(
                'pf-sheet relative flex w-full flex-col overflow-hidden sm:max-w-[20rem]',
                'bg-[var(--pf-surface)] shadow-[var(--pf-shadow-xl)] focus:outline-none',
                /*
                  گوشه‌ها در موبایل فقط بالا گرد است — لبه پایین به کف
                  صفحه چسبیده و گردکردنش فقط یک نوار نازک از پس‌زمینه
                  زیرش نشان می‌دهد.
                */
                'rounded-t-[1.25rem] sm:rounded-[1.25rem]',
                /*
                  ⚠️ در موبایل ارتفاع محدود می‌شود و بدنه اسکرول می‌گیرد.
                  روی صفحه‌های کوتاه (و وقتی صفحه‌کلید بالا می‌آید) کشوی
                  ۴۰۰ پیکسلی از بالای صفحه بیرون می‌زد و سرصفحه‌اش
                  دیده نمی‌شد.
                */
                'max-h-[90dvh] sm:max-h-none',
              )}
            >
              {/* ---------- سرصفحه ---------- */}
              <div className="flex shrink-0 flex-col gap-0.5 px-5 pt-5 pb-3">
                <h2 className="text-[length:var(--pf-text-base)] font-[var(--pf-weight-bold)] text-[var(--pf-text)]">
                  {title}
                </h2>

                {description && (
                  <p className="text-[length:var(--pf-text-xs)] text-[var(--pf-text-muted)]">
                    {description}
                  </p>
                )}
              </div>

              {/*
                ---------- پیمایش ----------

                ⚠️ ترتیب دکمه‌ها در چیدمان راست‌به‌چپ: فرزندِ **اول** سمت
                **راست** می‌نشیند. چون زمان در تقویم فارسی از راست به چپ
                پیش می‌رود، «ماه قبل» راست است و «ماه بعد» چپ — یعنی
                گذشته راست، آینده چپ.

                ⚠️ فلش‌ها فقط در نمای روز معنا دارند. در نمای ماه و سال
                جایشان خالی می‌ماند (`invisible` و نه حذف) تا عنوانِ وسط
                سرِ جایش بماند و با عوض‌شدن نما نپرد.
              */}
              <div className="flex shrink-0 items-center justify-between px-5 pb-3">
                <NavButton
                  label="ماه قبل"
                  onClick={() => step(-1)}
                  direction="prev"
                  hidden={mode !== 'day'}
                />

                {/*
                  🔴 عنوان، خودش دکمه است: کلیک روی «مرداد ۱۴۰۳» نمای ماه
                  را باز می‌کند و کلیک دوباره نمای سال. این تنها راهِ
                  رسیدن به تاریخ‌های دور است — پیمودن ۴۰ سال با فلشِ ماه
                  یعنی ~۴۸۰ کلیک.
                */}
                <button
                  type="button"
                  onClick={() => setMode(mode === 'day' ? 'month' : mode === 'month' ? 'year' : 'day')}
                  aria-label={
                    mode === 'day'
                      ? 'انتخاب ماه'
                      : mode === 'month'
                        ? 'انتخاب سال'
                        : 'بازگشت به روزها'
                  }
                  className={cn(
                    'flex items-center gap-1 rounded-full px-3 py-1',
                    'text-[length:var(--pf-text-md)] font-[var(--pf-weight-bold)] text-[var(--pf-text)]',
                    'transition-colors hover:bg-[var(--pf-surface-hover)]',
                  )}
                >
                  <span>
                    {mode === 'year'
                      ? `${toPersianDigits(years[0])} — ${toPersianDigits(years[years.length - 1])}`
                      : mode === 'month'
                        ? toPersianDigits(view.jy)
                        : `${JALALI_MONTHS[view.jm - 1]} ${toPersianDigits(view.jy)}`}
                  </span>

                  {/* فلشِ رو به پایین که با باز بودنِ نما برمی‌گردد. */}
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    className={cn(
                      'size-4 transition-transform',
                      mode !== 'day' && 'rotate-180',
                    )}
                    aria-hidden="true"
                  >
                    <path
                      d="M6 9l6 6 6-6"
                      stroke="currentColor"
                      strokeWidth="1.8"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <NavButton
                  label="ماه بعد"
                  onClick={() => step(1)}
                  direction="next"
                  hidden={mode !== 'day'}
                />
              </div>

              {/*
                ---------- بدنه ----------

                ⚠️ ارتفاع بدنه ثابت بسته شده و هر سه نما داخل همان جا
                می‌نشینند. بدون آن، رفتن از «روز» (۶ سطر) به «ماه» (۴
                سطر) کارت را جمع می‌کند و دکمه‌های پانوشت بالا می‌پرند —
                همان پرشی که برای عوض‌کردن ماه حل شد.

                عدد از بلندترین نما می‌آید — نمای روز: سرصفحه هفته
                (~۲۲px) + فاصله ۸px + ۶ سطر × ۳۶px + ۵ فاصله × ۴px
                ≈ ۲۶۶px. نمای ماه ۲۰۸px و نمای سال کوتاه‌تر است، پس هر
                دو داخل همین ارتفاع جا می‌شوند.
              */}
              {/*
                ⚠️ قفلِ ارتفاع از `sm:` شروع می‌شود. `min-h` بر `max-h`ی
                که روی کشو نشسته غلبه می‌کند، پس اگر بدون پیشوند بماند
                روی صفحه کوتاه بدنه کوچک نمی‌شود و پانوشت از کادر
                بیرون می‌افتد. در موبایل به‌جایش بدنه اسکرول می‌گیرد.
              */}
              <div className="flex-1 overflow-y-auto px-5 pb-5 sm:min-h-[16.65rem]">
                {mode === 'day' && (
                  <>
                  {/* روزهای هفته */}
                  <div className="mb-2 grid grid-cols-7 gap-1">
                    {JALALI_WEEKDAYS_SHORT.map((day, index) => (
                      <div
                        key={day}
                        className={cn(
                          'py-1 text-center text-[length:var(--pf-text-xs)] font-[var(--pf-weight-medium)]',
                          /* ⚠️ جمعه (ستون آخر) تعطیل است و باید در نگاه اول پیدا باشد. */
                          index === 6 ? 'text-[var(--pf-danger-text)]' : 'text-[var(--pf-text-muted)]',
                        )}
                      >
                        {day}
                      </div>
                    ))}
                  </div>

                  {/*
                    ⚠️ `grid-rows-6` لازم است و نه فقط خانه‌های خالی: ارتفاع
                    از خودِ دکمه‌ها می‌آید، ولی سطر ششمِ یک ماهِ ۵ سطری هیچ
                    دکمه‌ای ندارد و بدون آن صفر ارتفاع می‌گیرد.
                  */}
                  <div className="grid grid-cols-7 grid-rows-6 gap-1">
                    {cells.map((day, index) => {
                      if (day === null) {
                        return <div key={`empty-${index}`} />
                      }

                      const isSelected =
                        selected?.jy === view.jy &&
                        selected?.jm === view.jm &&
                        selected?.jd === day

                      const isToday = today.jy === view.jy && today.jm === view.jm && today.jd === day
                      const disabledDay = outOfRange(day)
                      const isFriday = index % 7 === 6

                      /*
                       | ⚠️ `has` و نه `get(...) != null`: عنوانِ مناسبت
                       | می‌تواند `null` باشد (روزِ تعطیلی که در تقویم
                       | سازمانی دستی علامت خورده و اسمی ندارد). آن روز
                       | هم تعطیل است و باید قرمز شود.
                       */
                      const iso = jalaliToDateString(view.jy, view.jm, day)
                      const isHoliday = holidays?.has(iso) ?? false
                      const holidayTitle = isHoliday ? (holidays?.get(iso) ?? null) : null

                      return (
                        <button
                          key={day}
                          type="button"
                          disabled={disabledDay}
                          aria-pressed={isSelected}
                          aria-current={isToday ? 'date' : undefined}
                          /*
                           | ⚠️ عنوان مناسبت هم در `title` می‌آید و هم در
                           | `aria-label`. فقط رنگ‌کردن، تعطیلی را برای
                           | صفحه‌خوان و برای کاربرِ نابینا به رنگ
                           | نامرئی می‌کرد.
                           */
                          title={holidayTitle ?? undefined}
                          aria-label={
                            holidayTitle
                              ? `${day} — تعطیل رسمی: ${holidayTitle}`
                              : isHoliday
                                ? `${day} — تعطیل`
                                : undefined
                          }
                          onClick={() => {
                          onChange(jalaliToDateString(view.jy, view.jm, day))
                          setOpen(false)
                        }}
                          className={cn(
                            /*
                             | دایره ۳۶ پیکسلی — یک پله کوچک‌تر از ۴۰
                             | پیکسلِ طرح. با فاصله ۴px بینشان، مرکز تا
                             | مرکزِ دو خانه ۴۰px می‌ماند، پس هدفِ لمس
                             | عملاً همان اندازه قبل است و فقط کارت
                             | جمع‌تر می‌شود.
                             */
                            'relative mx-auto flex size-9 items-center justify-center rounded-full',
                            'text-[length:var(--pf-text-sm)] transition-colors',
                            isSelected
                              ? cn(
                                  'bg-[var(--pf-primary-container)] font-[var(--pf-weight-bold)]',
                                  'text-[var(--pf-on-primary-container)] shadow-[var(--pf-shadow)]',
                                  'transition-transform active:scale-95',
                                )
                              : cn(
                                  /* ⚠️ تعطیل رسمی همان قرمزِ جمعه را می‌گیرد و
                                     نه رنگی تازه: برای کاربر هر دو یک چیزند —
                                     «این روز تعطیل است». دو قرمزِ متفاوت یعنی
                                     یک تمایزِ بی‌معنا که باید حدس زده شود. */
                                  isFriday || isHoliday
                                    ? 'text-[var(--pf-danger-text)]'
                                    : 'text-[var(--pf-text)]',
                                  'hover:bg-[var(--pf-primary-soft)]',
                                ),
                            /*
                             | ⚠️ «امروز» فقط یک نقطه زیر عدد می‌گیرد و نه
                             | پس‌زمینه. اگر پررنگ شود با روزِ انتخاب‌شده
                             | اشتباه گرفته می‌شود — کاربر فکر می‌کند تاریخی
                             | انتخاب کرده که نکرده.
                             */
                            !isSelected && isToday && 'font-[var(--pf-weight-bold)]',
                            disabledDay && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                          )}
                        >
                          {toPersianDigits(day)}

                          {/*
                            ⚠️ نقطهٔ «امروز» و نقطهٔ «تعطیل» یک جای واحد
                            دارند و نمی‌توانند هر دو کشیده شوند — روی هم
                            می‌افتادند. «امروز» اولویت دارد چون تعطیلی
                            از رنگِ خودِ عدد هم پیداست، ولی «امروز» جز
                            این نقطه نشانی ندارد.
                          */}
                          {!isSelected && (isToday || isHoliday) && (
                            <span
                              className={cn(
                                'absolute bottom-1.5 size-1 rounded-full',
                                isToday
                                  ? 'bg-[var(--pf-primary-container)]'
                                  : 'bg-[var(--pf-danger-text)]',
                              )}
                              aria-hidden="true"
                            />
                          )}
                        </button>
                      )
                    })}
                  </div>
                  </>
                )}

                {/*
                  ---------- نمای ماه ----------

                  ⚠️ انتخاب ماه هیچ تاریخی ثبت نمی‌کند و فقط `view` را
                  می‌برد و به نمای روز برمی‌گردد. کاربر «مرداد» را زده تا
                  روزهای مرداد را ببیند — ثبتِ خودکارِ روز اول ماه یعنی
                  تاریخی که او هرگز نخواست.
                */}
                {mode === 'month' && (
                  <div className="grid grid-cols-3 gap-2 pt-2">
                    {JALALI_MONTHS.map((name, index) => {
                      const jm = index + 1
                      const isCurrent = view.jm === jm
                      const isSelectedMonth = selected?.jy === view.jy && selected?.jm === jm
                      const blocked = monthOutOfRange(view.jy, jm)

                      return (
                        <button
                          key={name}
                          type="button"
                          disabled={blocked}
                          aria-pressed={isCurrent}
                          onClick={() => {
                            setView((c) => ({ ...c, jm }))
                            setMode('day')
                          }}
                          className={cn(
                            'flex h-11 items-center justify-center rounded-[var(--pf-radius-lg)]',
                            'text-[length:var(--pf-text-sm)] transition-colors',
                            isSelectedMonth
                              ? 'bg-[var(--pf-primary-container)] font-[var(--pf-weight-bold)] text-[var(--pf-on-primary-container)]'
                              : 'text-[var(--pf-text)] hover:bg-[var(--pf-primary-soft)]',
                            /* ماهِ در حال نمایش فقط حاشیه می‌گیرد تا با انتخاب‌شده قاطی نشود. */
                            !isSelectedMonth && isCurrent && 'ring-1 ring-[var(--pf-primary-container)]',
                            blocked && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                          )}
                        >
                          {name}
                        </button>
                      )
                    })}
                  </div>
                )}

                {/*
                  ---------- نمای سال ----------

                  ⚠️ فهرست سال اسکرول‌شونده است چون می‌تواند ۱۱۰ درایه
                  باشد. `scrollIntoView` روی سالِ فعلی اجرا می‌شود تا
                  کاربر وسطِ بازه باز شود و نه ابتدای آن — وگرنه برای
                  رسیدن به ۱۴۰۳ باید ۹۰ سال اسکرول کند.

                  ⚠️ سقف ارتفاع و اسکرولِ خودِ فهرست فقط در دسکتاپ است.
                  در موبایل بدنه‌ی کشو خودش اسکرول دارد و اسکرولِ
                  تودرتو یعنی کاربر بسته به اینکه انگشتش کجا باشد یکی
                  از آن دو را حرکت می‌دهد.
                */}
                {mode === 'year' && (
                  <div className="grid grid-cols-4 gap-2 pt-2 sm:max-h-[16rem] sm:overflow-y-auto">
                    {years.map((jy) => {
                      const isCurrent = view.jy === jy
                      const isSelectedYear = selected?.jy === jy
                      const blocked = yearOutOfRange(jy)

                      return (
                        <button
                          key={jy}
                          type="button"
                          disabled={blocked}
                          aria-pressed={isCurrent}
                          ref={isCurrent ? scrollTargetRef : undefined}
                          onClick={() => {
                            setView((c) => ({ ...c, jy }))
                            setMode('month')
                          }}
                          className={cn(
                            'flex h-10 items-center justify-center rounded-[var(--pf-radius-lg)]',
                            'text-[length:var(--pf-text-sm)] transition-colors',
                            isSelectedYear
                              ? 'bg-[var(--pf-primary-container)] font-[var(--pf-weight-bold)] text-[var(--pf-on-primary-container)]'
                              : 'text-[var(--pf-text)] hover:bg-[var(--pf-primary-soft)]',
                            !isSelectedYear && isCurrent && 'ring-1 ring-[var(--pf-primary-container)]',
                            blocked && 'cursor-not-allowed opacity-30 hover:bg-transparent',
                          )}
                        >
                          {toPersianDigits(jy)}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>

              {/*
                ---------- پانوشت ----------

                🔴 بدون «تأیید»: کلیک روی روز همان‌جا ثبت می‌کند و
                می‌بندد. یک کلیک به جای دو کلیک — و برای انتخابگری که
                کارش فقط گرفتن یک تاریخ است، مرحله تأییدِ اضافه ارزشی
                نداشت.

                ⚠️ «لغو» هم با آن رفت: وقتی چیزی تا لحظه کلیک ثبت
                نمی‌شود، دکمه‌ای برای لغو کردن لازم نیست. بستن با کلیک
                بیرون، Escape یا همان انتخاب روز انجام می‌شود.
              */}
              {selectedHolidayTitle && (
                <div
                  className={cn(
                    'flex shrink-0 items-center gap-2 border-t px-5 py-2.5',
                    'border-[var(--pf-border)] bg-[var(--pf-danger-50)]',
                    'text-[length:var(--pf-text-xs)] text-[var(--pf-danger-text)]',
                  )}
                >
                  <span className="size-1.5 shrink-0 rounded-full bg-[var(--pf-danger-text)]" aria-hidden="true" />
                  <span className="truncate">تعطیل رسمی — {selectedHolidayTitle}</span>
                </div>
              )}

              <div className="flex shrink-0 items-center justify-between border-t border-[var(--pf-border)] px-5 py-3">
                <button
                  type="button"
                  disabled={todayOutOfRange}
                  onClick={() => {
                    onChange(todayIso)
                    setOpen(false)
                  }}
                  className={cn(
                    'text-[length:var(--pf-text-sm)] font-[var(--pf-weight-medium)]',
                    'text-[var(--pf-text-muted)] transition-colors',
                    /* --pf-link — این متن است، نه پس‌زمینه دکمه */
                    'hover:text-[var(--pf-link)] disabled:opacity-40 disabled:hover:text-[var(--pf-text-muted)]',
                  )}
                >
                  امروز
                </button>

                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className={cn(
                    'rounded-full px-4 py-1.5 text-[length:var(--pf-text-sm)]',
                    'font-[var(--pf-weight-medium)] text-[var(--pf-text-muted)]',
                    'transition-colors hover:bg-[var(--pf-surface-hover)]',
                  )}
                >
                  بستن
                </button>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

/** نوکِ فلش سمت چپ — از x=۱۵ به x=۹ می‌رود و برمی‌گردد. */
const CHEVRON_LEFT = 'M15 6l-6 6 6 6'

/** نوکِ فلش سمت راست — از x=۹ به x=۱۵ می‌رود و برمی‌گردد. */
const CHEVRON_RIGHT = 'M9 6l6 6-6 6'

/**
 * دکمه پیمایش ماه — دایره ۳۲ پیکسلی طرح Stitch.
 *
 * ⚠️ در چیدمان راست‌به‌چپ، «ماه بعد» سمت چپ است و «ماه قبل» سمت راست —
 * برعکسِ چیدمان لاتین. پس فلش‌ها هم برعکسِ حالت لاتین‌اند: هر کدام به
 * بیرون از مرکز اشاره می‌کند، یعنی به سمتی که خودش آنجاست.
 *
 * ⚠️ نگاه‌کردن به جهت فلش کافی نیست؛ برچسب صریح `aria-label` برای
 * صفحه‌خوان لازم است.
 */
function NavButton({
  label,
  onClick,
  direction,
  hidden = false,
}: {
  label: string
  onClick: () => void
  direction: 'prev' | 'next'
  /** جای دکمه را نگه می‌دارد ولی نامرئی و غیرفعالش می‌کند. */
  hidden?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      /* ⚠️ از دسترس صفحه‌خوان و Tab هم خارج می‌شود، نه فقط از دید. */
      aria-hidden={hidden || undefined}
      tabIndex={hidden ? -1 : undefined}
      disabled={hidden}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-full',
        'text-[var(--pf-text-muted)] transition-colors',
        'hover:bg-[var(--pf-surface-hover)] hover:text-[var(--pf-text)]',
        hidden && 'invisible',
      )}
    >
      <svg viewBox="0 0 24 24" fill="none" className="size-5" aria-hidden="true">
        {/*
          `CHEVRON_LEFT` نوکش سمت چپ است و `CHEVRON_RIGHT` سمت راست.
          «بعد» چپ می‌نشیند پس به چپ اشاره می‌کند و «قبل» راست.
        */}
        <path
          d={direction === 'next' ? CHEVRON_LEFT : CHEVRON_RIGHT}
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </button>
  )
}
